import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  CalendarDays,
  LogOut,
  Settings,
  UserCircle,
  AlertTriangle,
  CreditCard,
  PauseCircle,
  Mail,
  Smartphone,
  ChevronDown,
  Briefcase,
  Coffee,
  Wrench,
  Mic,
} from 'lucide-react';
import { Logo } from '../components/Logo';
import NotificationBell from '../components/NotificationBell';
import WorkspaceSwitcher from '../components/WorkspaceSwitcher';
import TrialReminderBanner from './TrialReminderBanner';
import AccountInactiveNotice from './AccountInactiveNotice';
import VoiceRecorder from './VoiceRecorder';
import { syncVoiceQueue } from '../lib/voiceQueue';

// ---------------------------------------------------------------------------
// Grouped navigation — fewer top-level items, mobile-first labels.
// Each group has a primary page (`path`) and a dropdown of sub-pages.
// "Field" lives outside the groups as a prominent always-visible button so
// providers can jump to today's jobs with one tap.
// ---------------------------------------------------------------------------

const NAV_GROUPS = [
  {
    key: 'schedule',
    label: 'Schedule',
    icon: CalendarDays,
    path: '/dashboard',
    items: [
      { path: '/dashboard/field',      label: 'Today' },
      { path: '/dashboard',            label: 'Overview' },
      { path: '/dashboard/closeout',   label: 'Closeout' },
      { path: '/dashboard/weekly-review', label: 'Weekly Review' },
      { path: '/dashboard/calendar',   label: 'Calendar' },
      { path: '/dashboard/bookings',   label: 'Bookings' },
      { path: '/dashboard/availability', label: 'Hours' },
      { path: '/dashboard/route-zones', label: 'Routes' },
    ],
  },
  {
    key: 'notes',
    label: 'Voice Notes',
    icon: Mic,
    path: '/dashboard/notes',
    items: [
      { path: '/dashboard/notes',     label: 'Voice Notes' },
      { path: '/dashboard/reminders', label: 'Reminders' },
    ],
  },
  {
    key: 'clients',
    label: 'Clients',
    icon: UserCircle,
    path: '/dashboard/clients',
    items: [
      { path: '/dashboard/clients', label: 'Clients' },
    ],
  },
  {
    key: 'services',
    label: 'Services',
    icon: Wrench,
    path: '/dashboard/services',
    items: [
      { path: '/dashboard/services',      label: 'Services' },
      { path: '/dashboard/consultations', label: 'Consultations' },
    ],
  },
  {
    key: 'community',
    label: 'Community',
    icon: Coffee,
    path: '/dashboard/lounge',
    items: [
      { path: '/dashboard/lounge',    label: 'Lounge' },
      { path: '/dashboard/reviews',   label: 'Reviews' },
      { path: '/dashboard/invites',   label: 'Invites' },
      { path: '/dashboard/referrals', label: 'Referrals' },
    ],
  },
  {
    key: 'account',
    label: 'Account',
    icon: Briefcase,
    path: '/dashboard/settings',
    items: [
      { path: '/dashboard/settings', label: 'Settings' },
      { path: '/dashboard/invoice-settings', label: 'Invoices' },
    ],
  },
];

// All routes that belong to a given group — used to highlight the active tab
const ROUTE_TO_GROUP = NAV_GROUPS.reduce((acc, g) => {
  g.items.forEach((it) => {
    acc[it.path] = g.key;
  });
  return acc;
}, {});

export default function DashboardLayout({ children }) {
  const { user, logout, token } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Flush any voice notes recorded while offline once we're back online.
  useEffect(() => {
    if (!token) return;
    const flush = () => { if (navigator.onLine) syncVoiceQueue(token); };
    flush();
    window.addEventListener('online', flush);
    return () => window.removeEventListener('online', flush);
  }, [token]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const accountStatus = user?.accountStatus || 'PENDING';
  const isDeactivated = accountStatus === 'DEACTIVATED';
  const isPending = accountStatus === 'PENDING';
  const emailVerified = user?.emailVerified !== false;

  const activeGroupKey = ROUTE_TO_GROUP[location.pathname] || 'schedule';
  const isFieldActive = location.pathname === '/dashboard/field';

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-card border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center gap-6">
              <Logo variant="compact" size="sm" />
              <div className="hidden sm:block border-l pl-6">
                <h1 className="text-lg font-semibold" data-testid="dashboard-title">
                  {user?.businessName || 'Dashboard'}
                </h1>
                <p className="text-sm text-muted-foreground">{user?.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <WorkspaceSwitcher />
              <NotificationBell />
              <Button
                variant="outline"
                onClick={handleLogout}
                data-testid="logout-button"
              >
                <LogOut className="mr-2 h-4 w-4" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Grouped Navigation — Desktop */}
      <div className="bg-card border-b hidden md:block">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-1" data-testid="dashboard-nav">
            {NAV_GROUPS.map((g) => (
              <DesktopGroupTab
                key={g.key}
                group={g}
                active={activeGroupKey === g.key}
                navigate={navigate}
                currentPath={location.pathname}
              />
            ))}
            {/* Prominent Field shortcut — always visible, easy to find */}
            <Link
              to="/dashboard/field"
              className={`ml-auto flex items-center gap-2 px-4 py-2 my-2 rounded-md transition-colors ${
                isFieldActive
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-primary/10 text-primary hover:bg-primary/20'
              }`}
              data-testid="nav-field"
            >
              <Smartphone className="h-4 w-4" />
              <span className="font-medium">Field</span>
            </Link>
          </nav>
        </div>
      </div>

      {/* Trial / account-state banners — soft reminders only (Session 26).
          Hard "Activate for $19/month" banner removed for private beta;
          providers in trial or with lifetime access see no payment-blocking UI. */}
      {user && (
        <div className="bg-card border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 space-y-3">
            <TrialReminderBanner provider={user} />
            <AccountInactiveNotice provider={user} accountStatus={accountStatus} />
          </div>
        </div>
      )}

      {isDeactivated && (
        <div className="bg-red-50 border-b border-red-200" data-testid="deactivated-banner">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <PauseCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
                <div>
                  <p className="font-medium text-red-800">Account is paused (read-only)</p>
                  <p className="text-sm text-red-700">Reactivate in Settings to start taking bookings again.</p>
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="border-red-300 text-red-700 hover:bg-red-100 flex-shrink-0"
                onClick={() => navigate('/dashboard/settings')}
                data-testid="reactivate-cta"
              >
                <Settings className="mr-2 h-4 w-4" />
                Go to Settings
              </Button>
            </div>
          </div>
        </div>
      )}

      {!emailVerified && (
        <div className="bg-blue-50 border-b border-blue-200" data-testid="email-verification-banner">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-blue-600 flex-shrink-0" />
                <div>
                  <p className="font-medium text-blue-800">Verify your email to finish setup</p>
                  <p className="text-sm text-blue-700">Check your inbox for the link.</p>
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="border-blue-300 text-blue-700 hover:bg-blue-100 flex-shrink-0"
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('token');
                    await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/resend-verification`, {
                      method: 'POST',
                      headers: { 'Authorization': `Bearer ${token}` }
                    });
                    alert('Verification email sent!');
                  } catch (e) {
                    console.error(e);
                  }
                }}
                data-testid="resend-verification"
              >
                <Mail className="mr-2 h-4 w-4" />
                Resend
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile bottom nav — 5 groups + Field */}
      <MobileBottomNav
        navigate={navigate}
        activeGroupKey={activeGroupKey}
        isFieldActive={isFieldActive}
        currentPath={location.pathname}
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-32 md:pb-6">
        {children}
      </main>

      {/* Global voice-note recorder — available on every provider page */}
      <VoiceRecorder variant="fab" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Desktop: group button = navigates to primary path. If there are 2+ sub-items
// we add a small ▾ that opens a popover listing the rest.
// ---------------------------------------------------------------------------

function DesktopGroupTab({ group, active, currentPath }) {
  const Icon = group.icon;
  const hasMenu = group.items.length > 1;

  return (
    <div className="flex">
      <Link
        to={group.path}
        className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
          active
            ? 'border-primary text-primary font-semibold'
            : 'border-transparent text-muted-foreground hover:text-foreground'
        }`}
        data-testid={`nav-${group.key}`}
      >
        <Icon className="h-4 w-4" />
        {group.label}
      </Link>
      {hasMenu && (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={`px-1.5 border-b-2 transition-colors ${
                active
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
              aria-label={`${group.label} menu`}
              data-testid={`nav-${group.key}-menu`}
            >
              <ChevronDown className="h-4 w-4" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-48 p-1" align="start">
            <ul className="space-y-0.5">
              {group.items.map((it) => (
                <li key={it.path}>
                  <Link
                    to={it.path}
                    className={`block px-3 py-2 rounded text-sm hover:bg-muted ${
                      currentPath === it.path ? 'bg-muted font-medium' : ''
                    }`}
                    data-testid={`subnav-${it.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {it.label}
                  </Link>
                </li>
              ))}
            </ul>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mobile: 5 group icons across the bottom + Field as a 6th. Tapping a group
// with sub-items opens a small sheet via Popover anchored at that icon.
// ---------------------------------------------------------------------------

function MobileBottomNav({ navigate, activeGroupKey, isFieldActive, currentPath }) {
  const [openKey, setOpenKey] = useState(null);

  const onTapGroup = (g) => {
    if (g.items.length > 1) {
      setOpenKey(openKey === g.key ? null : g.key);
    } else {
      navigate(g.path);
      setOpenKey(null);
    }
  };

  return (
    <div className="md:hidden fixed bottom-0 left-0 right-0 bg-card border-t z-50">
      {/* Expanded sub-menu sheet */}
      {openKey && (
        <div className="border-t bg-card" data-testid={`mobile-submenu-${openKey}`}>
          <ul className="flex flex-wrap gap-1 p-2">
            {NAV_GROUPS.find((g) => g.key === openKey).items.map((it) => (
              <li key={it.path}>
                <button
                  type="button"
                  onClick={() => {
                    navigate(it.path);
                    setOpenKey(null);
                  }}
                  className={`px-3 py-2 rounded text-sm ${
                    currentPath === it.path
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-foreground'
                  }`}
                >
                  {it.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <nav className="flex items-stretch">
        {NAV_GROUPS.map((g) => {
          const Icon = g.icon;
          const active = activeGroupKey === g.key;
          return (
            <button
              key={g.key}
              type="button"
              onClick={() => onTapGroup(g)}
              className={`flex-1 flex flex-col items-center justify-center gap-1 py-2 px-1 min-w-0 ${
                active ? 'text-primary' : 'text-muted-foreground'
              }`}
              data-testid={`mobile-nav-${g.key}`}
            >
              <Icon className="h-5 w-5" />
              <span className="text-[10px] font-medium truncate w-full text-center">
                {g.label}
              </span>
            </button>
          );
        })}
        {/* Field — prominent shortcut */}
        <button
          type="button"
          onClick={() => navigate('/dashboard/field')}
          className={`flex-1 flex flex-col items-center justify-center gap-1 py-2 px-1 min-w-0 ${
            isFieldActive
              ? 'bg-primary text-primary-foreground'
              : 'bg-primary/10 text-primary'
          }`}
          data-testid="mobile-nav-field"
        >
          <Smartphone className="h-5 w-5" />
          <span className="text-[10px] font-bold truncate w-full text-center">Field</span>
        </button>
      </nav>
    </div>
  );
}
